import { useEffect, useRef, useState, type RefObject } from "react";
import {
  XMarkIcon,
  PencilIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  EllipsisVerticalIcon,
  ChevronRightIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import { useOnClickOutside } from "usehooks-ts";
import { Loader } from "../loader.js";
import { relativeTime, type ConversationRow } from "./helpers.js";

export interface HistoryViewProps {
  conversations: ConversationRow[];
  isLoading: boolean;
  isFetching: boolean;
  nextCursor: string | null;
  isMobile: boolean;
  isExpanded: boolean;
  historyListRef: RefObject<HTMLDivElement>;
  onSelectConversation: (conversation: ConversationRow) => void;
  onNewChat: () => void;
  onLoadMore: () => void;
  onToggleExpanded: () => void;
  onClose: () => void;
  onRenameConversation: (conversationId: string, title: string) => Promise<void>;
  onDeleteConversation: (conversationId: string) => Promise<void>;
}

export function HistoryView({
  conversations,
  isLoading,
  isFetching,
  nextCursor,
  isMobile,
  isExpanded,
  historyListRef,
  onSelectConversation,
  onNewChat,
  onLoadMore,
  onToggleExpanded,
  onClose,
  onRenameConversation,
  onDeleteConversation,
}: HistoryViewProps) {
  return (
    <>
      {/* History header */}
      <div className="flex items-center justify-between border-b border-gray px-4 py-3">
        <h2 className="text-base font-semibold text-midnight">Your Chats</h2>
        <div className="flex items-center gap-1">
          {!isMobile && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8"
              aria-label={isExpanded ? "Collapse" : "Expand"}>
              {isExpanded ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8"
              aria-label="Close chat">
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Conversation list */}
      <div ref={historyListRef} className="flex-1 overflow-y-auto">
        {isLoading && conversations.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Loader size="small" delayMs={null} />
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <SparklesIcon className="h-8 w-8 text-gray-60/50" />
            <p className="text-sm text-gray-60">No conversations yet.</p>
          </div>
        ) : (
          <>
            {conversations.map(conv => (
              <ConversationListItem
                key={conv.id}
                conversation={conv}
                onSelect={() => onSelectConversation(conv)}
                onRename={title => onRenameConversation(conv.id, title)}
                onDelete={() => onDeleteConversation(conv.id)}
              />
            ))}
            {nextCursor && (
              <div className="flex justify-center py-3">
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={isFetching}
                  className="text-xs font-medium text-lac-leman transition hover:underline disabled:opacity-50">
                  {isFetching ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* New chat action */}
      <div className="flex justify-end border-t border-gray p-4">
        <button
          type="button"
          onClick={onNewChat}
          className="flex items-center gap-2 rounded-full bg-lac-leman px-5 py-2.5 text-sm font-medium text-white transition hover:brightness-110">
          <PencilSquareIcon className="h-4 w-4" />
          New Chat
        </button>
      </div>
    </>
  );
}

interface ConversationListItemProps {
  conversation: ConversationRow;
  onSelect: () => void;
  onRename: (title: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

function ConversationListItem({ conversation, onSelect, onRename, onDelete }: ConversationListItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Close the menu on outside clicks; the toggle button is included so re-clicking it doesn't reopen it.
  useOnClickOutside([menuRef, menuButtonRef] as RefObject<HTMLElement>[], () => {
    setMenuOpen(false);
    setConfirmingDelete(false);
  });

  useEffect(() => {
    if (isEditing) titleInputRef.current?.focus();
  }, [isEditing]);

  const startEditing = () => {
    setEditTitle(conversation.title);
    setRenameError(null);
    setIsEditing(true);
    setMenuOpen(false);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setRenameError(null);
  };

  const saveTitle = async () => {
    const title = editTitle.trim();
    if (!title || title === conversation.title) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setRenameError(null);
    try {
      await onRename(title);
      setIsEditing(false);
    } catch {
      setRenameError("Something went wrong. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
      setMenuOpen(false);
      setConfirmingDelete(false);
    } catch {
      setDeleteError("Something went wrong. Please try again.");
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative border-b border-gray/60">
      <div
        role="button"
        tabIndex={0}
        onClick={() => !isEditing && onSelect()}
        onKeyDown={e => {
          if (isEditing || e.key !== "Enter") return;
          onSelect();
        }}
        className="flex w-full cursor-pointer items-center gap-3 px-4 py-3.5 text-left transition hover:bg-lac-leman-4">
        <div className="flex-1 overflow-hidden">
          {isEditing ? (
            <input
              ref={titleInputRef}
              type="text"
              value={editTitle}
              disabled={isSaving}
              onClick={e => e.stopPropagation()}
              onChange={e => setEditTitle(e.target.value)}
              onKeyDown={e => {
                e.stopPropagation();
                if (e.key === "Enter") saveTitle();
                if (e.key === "Escape") cancelEditing();
              }}
              className="w-full border border-midnight px-2 py-1 text-sm font-medium text-midnight focus:outline-none focus:ring-2 focus:ring-lac-leman"
            />
          ) : (
            <>
              <p className="truncate text-sm font-medium text-midnight">
                {conversation.title || <span className="italic text-gray-60">Untitled</span>}
              </p>
              <p className="mt-0.5 text-xs text-gray-60">
                {relativeTime(conversation.lastMessageAt ?? conversation.created)}
              </p>
            </>
          )}
          {isEditing && renameError && <p className="mt-1 text-xs text-error">{renameError}</p>}
        </div>

        {isEditing ? (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              disabled={isSaving}
              onClick={e => {
                e.stopPropagation();
                saveTitle();
              }}
              aria-label="Save title"
              className="rounded-lg p-1.5 text-lac-leman transition hover:bg-lac-leman-8 disabled:opacity-50">
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={e => {
                e.stopPropagation();
                cancelEditing();
              }}
              aria-label="Cancel rename"
              className="rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8 disabled:opacity-50">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              ref={menuButtonRef}
              type="button"
              onClick={e => {
                e.stopPropagation();
                setMenuOpen(prev => !prev);
              }}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="shrink-0 rounded-lg p-1.5 text-gray-80 transition hover:bg-lac-leman-8">
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>
            <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-60" />
          </div>
        )}
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            ref={menuRef}
            layout
            onKeyDown={e => {
              if (e.key !== "Escape") return;
              e.stopPropagation();
              setMenuOpen(false);
              setConfirmingDelete(false);
              menuButtonRef.current?.focus();
            }}
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            style={{ transformOrigin: "top right" }}
            className="absolute right-10 top-3 z-20 w-40 overflow-hidden rounded-lg border border-gray bg-white shadow-lg">
            <AnimatePresence mode="wait" initial={false}>
              {confirmingDelete ? (
                <motion.div
                  key="confirm"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12, ease: "easeOut" }}
                  className="p-3">
                  <p className="text-sm text-midnight">Delete this chat?</p>
                  {deleteError && <p className="mt-1.5 text-xs text-error">{deleteError}</p>}
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      disabled={isDeleting}
                      onClick={() => setConfirmingDelete(false)}
                      className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-80 transition hover:bg-lac-leman-8 disabled:opacity-50">
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={isDeleting}
                      onClick={handleDelete}
                      className="rounded-md bg-error/10 px-3 py-1.5 text-xs font-medium text-error transition hover:bg-error/20 disabled:opacity-50">
                      Delete
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="actions"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12, ease: "easeOut" }}
                  className="py-1">
                  <button
                    type="button"
                    onClick={startEditing}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-midnight transition hover:bg-lac-leman-4">
                    <PencilIcon className="h-4 w-4" />
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(true)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-lac-leman-4">
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
